import React from 'react';
import 'katex/dist/katex.min.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import { useSelector } from 'react-redux';

// Accessibility foundations
import SkipNav from './components/Common/SkipNav';
import LiveAnnouncer from './components/Common/LiveAnnouncer';
import GlobalToast from './components/Common/GlobalToast';
import { loadStudioPrefs, applyStudioPreferences } from './components/Common/StudioSettingsPanel';

// Common Components
import ProtectedRoute from './components/Common/ProtectedRoute';
import WhitelabelHeader from './components/Common/WhitelabelHeader';

// Standard Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SystemAdminDashboard from './pages/SystemAdminDashboard';
import TenantAdminDashboard from './pages/TenantAdminDashboard';
import AdvancedEditor from './components/Projects/AdvancedEditor';
import ConceptGraphViewer from './components/Projects/ConceptGraphViewer';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import FAQ from './pages/Resources/FAQ';
import UserGuide from './pages/Resources/UserGuide';
import BestPractices from './pages/Resources/BestPractices';
import TermsOfUse from './pages/Legal/TermsOfUse';
import AIEthicsPolicy from './pages/Legal/AIEthicsPolicy';
import DPA from './pages/Legal/DPA';
import SecurityCompliance from './pages/Legal/SecurityCompliance';
import ContentStandards from './pages/Resources/ContentStandards';
import TaxonomyStandards from './pages/Resources/TaxonomyStandards';
import ProjectAcademicProfile from './components/Projects/ProjectAcademicProfile';
import AssessmentBlueprintPage from './components/Projects/AssessmentBlueprintPage';
import AssessmentBankView from './components/Projects/AssessmentBankView';
import AssessmentHistory from './components/Projects/AssessmentHistory';
import RegistrationPage from './pages/RegistrationPage';
import RegistrationThankYou from './pages/RegistrationThankYou';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ReleaseNotesPage from './pages/Resources/ReleaseNotesContainer';
import VerifyFacultyPage from './pages/VerifyFacultyPage';
import ClassroomRegistrationPage from './pages/ClassroomRegistrationPage';
import AskOS      from './pages/AskOS';
import AskOSAdmin from './pages/AskOSAdmin';
import SamkalpAskOS from './askos/SamkalpAskOS';
import SamkalpAdmin from './samkalp-admin/SamkalpAdmin';
import SamkalpFaculty from './samkalp-faculty/SamkalpFaculty';
import HostLivePage from './samkalp-faculty/liveSessions/HostLivePage';
import JoinLivePage from './askos/liveSessions/JoinLivePage';
import FacultyMentoringCallPage from './samkalp-faculty/mentoring/FacultyMentoringCallPage';
import StudentMentoringCallPage from './askos/mentoring/StudentMentoringCallPage';
import AcademicEvidenceDashboards from './pages/AcademicEvidenceDashboards';

// ExaminationOS — standalone module mounted at /examination/*
import ExaminationApp from './examination/ExaminationApp';


// Classroom Pages
import ClassroomLoginPage from './pages/ClassroomLoginPage';
import TeachingCopilot from './components/Projects/TeachingCopilot';
import TeachingCopilotChat from './components/Projects/TeachingCopilotChat';
import QuizPage from './pages/QuizPage';
import QuizMonitorPage from './pages/QuizMonitorPage';

/* -----------------------------
   Public Route (Auto-Forwarder)
------------------------------ */
// Prevents logged-in users from sitting on /login. 
// It forces them to the RootRedirect hub.
const PublicRoute = ({ children }) => {
    const { user } = useSelector((state) => state.user || {});
    if (user) {
        return <Navigate to="/" replace />;
    }
    return children;
};

/* -----------------------------
   Role-based Root Redirect
------------------------------ */
// The Hub: Decides exactly which dashboard to load based on Role and Context
const RootRedirect = () => {
    const { user, appContext } = useSelector((state) => state.user || {});

    if (!user) return <Navigate to="/login" replace />;

    // 1. Classroom Context
    if (appContext === 'classroom') {
        return <Navigate to={`/tenant/${user.tenant_slug}/copilot`} replace />;
    }

    // 2. Studio Context: System Admin
    if (user.role === 'system_admin') {
        return <Navigate to="/system-admin" replace />;
    }

    // 3. Studio Context: Tenant Admin
    if (user.role === 'tenant_admin') {
        return <Navigate to={`/tenant/${user.tenant_slug}/dashboard`} replace />;
    }

    // 4. Studio Context: Standard SME/Production
    return <Navigate to="/dashboard" replace />;
};

/* -----------------------------
   App Router
------------------------------ */
// Apply saved studio preferences on every page load so theme/font are consistent
// even before the WhitelabelHeader mounts.  Runs once at module evaluation time.
try { applyStudioPreferences(loadStudioPrefs()); } catch (_) {}

function App() {
    const { user, appContext } = useSelector((state) => state.user || {});

    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            {/* Accessibility: skip-navigation, live announcer, global toast */}
            <SkipNav />
            <LiveAnnouncer />
            <GlobalToast />

            {/* Header dynamically adapts branding and context */}
            {user && <WhitelabelHeader />}

            {/* SkipNav target anchor — WCAG 2.4.1 (Bypass Blocks) */}
            {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
            <a id="main-content" tabIndex={-1} aria-hidden="true" style={{ display: 'block', height: 0 }} />
            <Routes>
                {/* -------- Public Routes (Wrapped in PublicRoute) -------- */}
                <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                <Route path="/classroom-login" element={<PublicRoute><ClassroomLoginPage /></PublicRoute>} />
                <Route path="/classroom-register" element={<ClassroomRegistrationPage />} />
                
                <Route path="/register" element={<RegistrationPage />} />
                <Route path="/registration-thank-you" element={<RegistrationThankYou />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/release-notes" element={<ReleaseNotesPage />} />
                <Route path="/verify-faculty" element={<VerifyFacultyPage />} />

                {/* -------- Central Entry Point -------- */}
                <Route path="/" element={<RootRedirect />} />

                {/* -------- Studio Context (Production) -------- */}
                <Route 
                    path="/dashboard" 
                    element={
                        <ProtectedRoute context="studio">
                            <DashboardPage />
                        </ProtectedRoute>
                    } 
                />

                <Route 
                    path="/tenant/:slug/dashboard" 
                    element={
                        <ProtectedRoute context="studio" role="tenant_admin">
                            <TenantAdminDashboard />
                        </ProtectedRoute>
                    } 
                />

                <Route path="/editor/:projectId/:unitId" element={<ProtectedRoute context="studio"><AdvancedEditor /></ProtectedRoute>} />
                <Route path="/graph/:projectId" element={<ProtectedRoute context="studio"><ConceptGraphViewer /></ProtectedRoute>} />
                <Route path="/tenant/:slug/projects/:projectId/academic-profile" element={<ProtectedRoute context="studio"><ProjectAcademicProfile /></ProtectedRoute>} />
                <Route path="/tenant/:slug/projects/:projectId/blueprint" element={<ProtectedRoute context="studio"><AssessmentBlueprintPage /></ProtectedRoute>} />
                <Route path="/tenant/:slug/projects/:projectId/assessment-history" element={<ProtectedRoute context="studio"><AssessmentHistory /></ProtectedRoute>} />
                <Route path="/tenant/:slug/projects/:projectId/assessment-bank" element={<ProtectedRoute context="studio"><AssessmentBankView /></ProtectedRoute>} />
                {/* Evidence dashboards: all studio roles can read; V2 tabs are
                    gated in the component itself based on role */}
                <Route
                    path="/tenant/:slug/projects/:projectId/evidence"
                    element={
                        <ProtectedRoute context="studio">
                            <AcademicEvidenceDashboards />
                        </ProtectedRoute>
                    }
                />

                {/* -------- Classroom Context (Delivery) -------- */}
                <Route 
                    path="/tenant/:slug/copilot" 
                    element={
                        <ProtectedRoute context="classroom">
                            <TeachingCopilot />
                        </ProtectedRoute>
                    } 
                />

                <Route 
                    path="/tenant/:slug/copilot/:projectId/:unitId" 
                    element={
                        <ProtectedRoute context="classroom">
                            <TeachingCopilotChat />
                        </ProtectedRoute>
                    } 
                />

                {/* -------- System Admin -------- */}
                <Route 
                    path="/system-admin" 
                    element={
                        <ProtectedRoute context="studio" role="system_admin">
                            <SystemAdminDashboard />
                        </ProtectedRoute>
                    } 
                />

               {/* -------- AskOS — Student AI Professor -------- */}
                {/* /tenant/:slug/askos  → slug pre-filled from URL, still editable */}
                {/* /askos               → student types their slug in the form      */}
                <Route path="/tenant/:slug/askos"       element={<AskOS />} />
                <Route path="/askos"                    element={<AskOS />} />

                {/* -------- Samkalp IAS Academy — dedicated slug-free entry -------- */}
                <Route path="/samkalp-ias/*"             element={<SamkalpAskOS />} />

                {/* -------- Samkalp IAS Academy — unified Admin Dashboard (new, parallel) -------- */}
                <Route path="/samkalp-ias/admin/*"       element={<SamkalpAdmin />} />

                {/* -------- Samkalp IAS Academy — Faculty Dashboard (new, parallel) -------- */}
                <Route path="/samkalp-ias/faculty/*"     element={<SamkalpFaculty />} />

                {/* -------- Live Sessions — full-bleed broadcast/attend pages (bypass dashboard chrome) -------- */}
                <Route path="/samkalp-ias/faculty/live/:sessionId/host" element={<HostLivePage />} />
                <Route path="/samkalp-ias/live/:sessionId"              element={<JoinLivePage />} />

                {/* -------- 1:1 Mentoring calls — same full-bleed pattern -------- */}
                <Route path="/samkalp-ias/faculty/mentoring/:slotId/call" element={<FacultyMentoringCallPage />} />
                <Route path="/samkalp-ias/mentoring/:slotId/call"         element={<StudentMentoringCallPage />} />

                {/* -------- AskOS Admin — Student Management -------- */}
                {/* /tenant/:slug/askos-admin  → slug pre-filled from URL            */}
                {/* /askos-admin               → admin types their slug in the form  */}
                <Route path="/tenant/:slug/askos-admin" element={<AskOSAdmin />} />
                <Route path="/askos-admin"              element={<AskOSAdmin />} />

                {/* -------- Resource Pages (Public/Mixed) -------- */}
                <Route path="/user-guide" element={<UserGuide />} />
                <Route path="/best-practices" element={<BestPractices />} />
                <Route path="/terms" element={<TermsOfUse />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/content-standards" element={<ContentStandards />} />
                <Route path="/taxonomy-standards" element={<TaxonomyStandards />} />
                <Route path="/ai-ethics-policy" element={<AIEthicsPolicy />} />
                <Route path="/dpa" element={<DPA />} />
                <Route path="/security-compliance" element={<SecurityCompliance />} />

                {/* -------- ExaminationOS Module -------- */}
                <Route path="/examination/*" element={<ExaminationApp />} />

                {/* -------- Public Quiz Route (no login required) -------- */}
                <Route path="/quiz/:quizId" element={<QuizPage />} />

                {/* -------- Faculty Quiz Monitor (public, no login) -------- */}
                <Route path="/quiz/:quizId/monitor" element={<QuizMonitorPage />} />

                {/* -------- Fallback -------- */}
                <Route path="*" element={<Navigate to={appContext === 'classroom' ? "/classroom-login" : "/login"} replace />} />
            </Routes>
        </Router>
    );
}

export default App;